import React, { useState, useMemo, useEffect, useRef, useContext } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine, Scatter,
  ComposedChart,
} from 'recharts';
import { themeColors } from '../theme';
import PlotExportBar from './PlotExportBar';
import BandgapDefectSchematic from './BandgapDefectSchematic';
import { defectToTrapRow, fmtCopy } from './ParameterLibraryPanel';
import { assignLabelSlots } from '../utils/labelLayout';
import { WorkspaceContextSetter } from '../context/WorkspaceContext';

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1`;

// hc/q — E[eV] = 1239.84193 / λ[nm]; same constant as PLAnalysisPanel so
// the eV↔nm view toggle converts identically in both panels.
const EV_NM = 1239.84193;

// ─── Experimental TRPL parsing helpers ──────────────────────────────────────
// Accepts CSV with / without header, tab- or space-separated text, and
// comment lines beginning with # or %.  Time unit is auto-detected from
// the time span (tMax heuristic) unless the user overrides it via the
// unit selector; intensity is normalized to [0, 1] by dividing by the max.
const TRPL_UNIT_SCALE = { ps: 1 / 1000, ns: 1, us: 1000 };   // → ns
const TRPL_UNIT_LABEL = { ps: 'ps', ns: 'ns', us: 'µs' };

// tMax-threshold heuristic — can be wrong for short ns traces or long
// µs traces, which is why the detected unit is shown and overridable.
function detectTRPLUnit(tMax) {
  if (tMax > 5000)     return 'ps';
  if (tMax >= 10)      return 'ns';
  return 'us';
}

// `unit` = 'auto' | 'ps' | 'ns' | 'us'.  Returns
// { rows, detectedUnit, unit } or null when unparseable.
function parseTRPLFile(text, unit = 'auto') {
  const rows = [];
  const lines = String(text).split(/\r?\n/);
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    if (line.startsWith('#') || line.startsWith('%')) continue;
    // Split on comma, tab, semicolon, or whitespace
    const parts = line.split(/[\s,;\t]+/).filter(Boolean);
    if (parts.length < 2) continue;
    const t = Number(parts[0]);
    const i = Number(parts[1]);
    if (!Number.isFinite(t) || !Number.isFinite(i)) continue;   // header row
    rows.push([t, i]);
  }
  if (rows.length < 2) return null;
  const times = rows.map(r => r[0]);
  const ints  = rows.map(r => r[1]);
  const tMax = Math.max(...times.map(Math.abs));
  const detectedUnit = detectTRPLUnit(tMax);
  const effectiveUnit = unit === 'auto' ? detectedUnit : unit;
  const scale = TRPL_UNIT_SCALE[effectiveUnit];
  const iMax = Math.max(...ints.map(Math.abs), 1e-300);
  return {
    rows: rows
      .map(([t, i]) => ({ t_ns: t * scale, I: Math.max(0, i / iMax) }))
      .sort((a, b) => a.t_ns - b.t_ns),
    detectedUnit,
    unit: effectiveUnit,
  };
}

// Weighted fit residual between a bi-exponential fit (τ₁, τ₂, A₁/A₂
// ratio) and experimental (t, I) points, evaluated on the experimental
// times: mean of (I_fit−I)²/max(I,1e-6).  NOTE: this is NOT a reduced
// χ² — the data are normalized (not counts), there is no σ² and no
// degrees-of-freedom correction.  Displayed as "fit residual (weighted)".
function chiSquared(experimental, tau1, tau2, ratio) {
  if (!experimental || !experimental.length) return null;
  const r = Math.max(ratio, 1e-6);
  const A1 = r / (1 + r), A2 = 1 / (1 + r);
  let s = 0, n = 0;
  for (const { t_ns, I } of experimental) {
    const I_fit = A1 * Math.exp(-t_ns / tau1) + A2 * Math.exp(-t_ns / tau2);
    const denom = Math.max(I, 1e-6);
    s += (I_fit - I) * (I_fit - I) / denom;
    n += 1;
  }
  return n > 0 ? s / n : null;
}

// Coarse grid-search auto-fit over log-spaced τ₁, τ₂, A₁/A₂.  Returns the
// (τ₁, τ₂, ratio, residual) tuple that minimizes the weighted residual on
// the experimental points, plus `atBoundary` listing any parameter that
// landed on a search-grid edge (i.e. the true optimum may lie outside the
// searched range).  The τ grids step by ~1.62× between candidates, so
// fitted lifetimes are only defined to roughly ±30% — display precision
// should reflect that.
function autoFitGrid(experimental) {
  if (!experimental || experimental.length < 3) return null;
  const nT1 = 20, nT2 = 20, nR = 10;
  const T1_LO = 0.1,  T1_HI = 1000;
  const T2_LO = 1,    T2_HI = 10000;
  const R_LO  = 0.01, R_HI  = 100;
  const logspace = (a, b, n) => Array.from({ length: n }, (_, i) =>
    a * Math.pow(b / a, i / (n - 1)));
  const t1Grid = logspace(T1_LO, T1_HI, nT1);
  const t2Grid = logspace(T2_LO, T2_HI, nT2);
  const rGrid  = logspace(R_LO,  R_HI,  nR);
  let best = { tau1: 10, tau2: 100, ratio: 1, chi: Infinity };
  for (const t1 of t1Grid) {
    for (const t2 of t2Grid) {
      if (t2 <= t1) continue;   // τ₂ must be the slower component
      for (const r of rGrid) {
        const x = chiSquared(experimental, t1, t2, r);
        if (x != null && x < best.chi)
          best = { tau1: t1, tau2: t2, ratio: r, chi: x };
      }
    }
  }
  const eq = (a, b) => Math.abs(a - b) < Math.abs(b) * 1e-9;
  const atBoundary = [];
  if (eq(best.tau1, T1_LO) || eq(best.tau1, T1_HI))
    atBoundary.push(`τ₁ = ${best.tau1} ns`);
  if (eq(best.tau2, T2_LO) || eq(best.tau2, T2_HI))
    atBoundary.push(`τ₂ = ${best.tau2} ns`);
  if (eq(best.ratio, R_LO) || eq(best.ratio, R_HI))
    atBoundary.push(`A₁/A₂ = ${best.ratio}`);
  return { ...best, atBoundary };
}

const B_RAD = 2e-10; // CdTe radiative recombination coefficient [cm³/s]

// axisStyle / tickStyle are created inside PLPanel via useTheme() so they
// pick up the current theme's axis color reactively.

function makePLTooltip(c) {
  return function PLTooltip({ active, payload, label, xLabel, yLabel }) {
    if (!active || !payload?.length) return null;
    return (
      <div style={{
        background: c.bgCard, border: `1px solid ${c.border}`, borderRadius: 4,
        padding: '10px 14px', fontFamily: 'IBM Plex Mono, monospace', fontSize: 13,
      }}>
        <div style={{ color: c.textMuted, marginBottom: 6 }}>{xLabel} = {Number(label).toFixed(3)}</div>
        {payload.map(p => (
          <div key={p.name} style={{ color: p.color, marginBottom: 2 }}>
            {p.name}: {Number(p.value).toExponential(3)}
          </div>
        ))}
      </div>
    );
  };
}

function computePL(profiles, trapLevels) {
  if (!profiles?.length) return null;

  // Collect unique Eg values (layer bandgaps)
  const egSet = new Map();
  let totalRrad = 0;

  for (let i = 0; i < profiles.length; i++) {
    const pt = profiles[i];
    if (!pt.Eg || pt.Eg > 4.0) continue; // skip wide-bandgap window layers
    const ni = pt.ni || 1e6;
    const np_prod = pt.n * pt.p;
    const ni2 = ni * ni;
    const Rrad = B_RAD * Math.max(np_prod - ni2, 0);

    // Integrate: trapezoidal rule
    let dx_cm = 1e-7; // default 1nm
    if (i > 0) dx_cm = (profiles[i].x_nm - profiles[i - 1].x_nm) * 1e-7;
    if (dx_cm <= 0 && i < profiles.length - 1) dx_cm = (profiles[i + 1].x_nm - profiles[i].x_nm) * 1e-7;
    if (dx_cm <= 0) dx_cm = 1e-7;

    const contribution = Rrad * dx_cm;
    totalRrad += contribution;

    // Group by Eg (rounded to 0.01 eV)
    const egKey = Math.round(pt.Eg * 100) / 100;
    egSet.set(egKey, (egSet.get(egKey) || 0) + contribution);
  }

  // Build PL spectrum: Gaussian peaks at each Eg
  const peaks = [];
  for (const [eg, intensity] of egSet) {
    if (intensity > 0) peaks.push({ eV: eg, intensity, label: 'Band-to-band' });
  }

  // Add sub-bandgap peaks from trap levels
  if (trapLevels?.length) {
    for (const trap of trapLevels) {
      // Trap emission: photon energy = Eg - Et (for traps near valence band)
      // or Et (for traps near conduction band)
      const hvTrap = trap.reference === 'from_Ev'
        ? trap.Eg - trap.Et_eV   // CB → trap transition
        : trap.Et_eV;            // trap → VB transition
      if (hvTrap > 0.5 && hvTrap < trap.Eg) {
        // Trap PL intensity scales with Nt * sigma (relative to band-to-band)
        const bbPeak = peaks.find(p => Math.abs(p.eV - trap.Eg) < 0.05);
        const bbIntensity = bbPeak ? bbPeak.intensity : (totalRrad || 1e10);
        // Trap PL is typically 1-3 orders weaker than band-to-band
        const trapIntensity = bbIntensity * Math.min(trap.Nt * trap.sigma_n / 1e-1, 0.3);
        peaks.push({
          eV: hvTrap, intensity: trapIntensity,
          label: `${trap.trap_type} trap (${trap.layer})`,
        });
      }
    }
  }

  peaks.sort((a, b) => a.eV - b.eV);
  if (peaks.length === 0) return null;

  // Generate spectrum over a DATA-DRIVEN energy grid, Gaussian FWHM
  // ~ 2kT ≈ 52 meV at 300K. The grid must cover every peak — including
  // trap emission down to the 0.5 eV filter above — so the floor tracks
  // the lowest peak (0.3 eV margin, never above the legacy 0.8 eV start,
  // clamped ≥ 0.2 eV) instead of the old hardcoded eMin = 0.8.
  const sigma = 0.022; // ~52 meV FWHM / 2.355
  const trapSigma = 0.04; // broader for trap emission (~100 meV FWHM)
  const eMin = Math.max(0.2, Math.min(0.8, Math.min(...peaks.map(p => p.eV)) - 0.3));
  const eMax = Math.max(...peaks.map(p => p.eV)) + 0.5;
  const nPts = 200;
  const spectrum = [];
  for (let i = 0; i <= nPts; i++) {
    const hv = eMin + (eMax - eMin) * i / nPts;
    let I = 0;
    for (const pk of peaks) {
      const s = pk.label === 'Band-to-band' ? sigma : trapSigma;
      I += pk.intensity * Math.exp(-0.5 * ((hv - pk.eV) / s) ** 2);
    }
    spectrum.push({ hv, I });
  }

  // Normalize to peak = 1, keeping the raw (a.u.) values alongside so the
  // panel can toggle normalized ↔ raw display.
  const Imax = Math.max(...spectrum.map(s => s.I), 1e-100);
  for (const s of spectrum) { s.I_raw = s.I; s.I /= Imax; }

  return { spectrum, Imax, peaks, totalRrad,
           hasTraps: (trapLevels?.length || 0) > 0 };
}

function computeTRPL(profiles) {
  if (!profiles?.length) return null;

  // Separate absorber nodes (Eg < 2 eV) into depletion and bulk regions
  const absorber = profiles.filter(pt => pt.Eg && pt.Eg < 2.0);
  if (absorber.length < 3) return null;

  // Identify p-type absorber nodes
  const pType = absorber.filter(pt => (pt.Na || 0) > (pt.Nd || 0));
  // Effective minority carrier lifetime at each absorber node
  // p-type: minority = electrons, use tau_n
  // n-type: minority = holes, use tau_p
  const lifetimes = absorber.map(pt => {
    const isPType = (pt.Na || 0) > (pt.Nd || 0);
    const tau_bulk = isPType ? (pt.tau_n || 1e-9) : (pt.tau_p || 1e-9);
    // Effective lifetime including recombination: tau_eff = excess_carriers / R
    // In depletion region, R is dominated by drift, tau_eff is shorter
    const minority = isPType ? pt.n : pt.p;
    const ni = pt.ni || 1e6;
    const excess = Math.max(minority - ni * ni / Math.max(isPType ? pt.Na : pt.Nd, 1), 0);
    const tau_eff = (excess > 1e-10 && pt.R > 1e-10)
      ? Math.min(excess / pt.R, tau_bulk)
      : tau_bulk;
    return { ...pt, tau_eff, tau_bulk, isPType };
  });

  // Split into depletion (near junction, high E-field) and bulk (low E-field)
  const eFieldThreshold = 1e3; // V/cm — above this is depletion region
  const depletion = lifetimes.filter(pt =>
    pt.E_field != null && Math.abs(pt.E_field) > eFieldThreshold);
  const bulk = lifetimes.filter(pt =>
    pt.E_field == null || Math.abs(pt.E_field) <= eFieldThreshold);

  // Fast lifetime: average in depletion region
  const tauFast = depletion.length > 0
    ? depletion.reduce((s, pt) => s + pt.tau_eff, 0) / depletion.length
    : null;

  // Slow lifetime: G-weighted average in bulk absorber
  const bulkAbs = bulk.filter(pt => pt.Eg < 2.0);
  let tauSlow, tauSlowNum = 0, tauSlowDen = 0;
  for (const pt of bulkAbs) {
    if (pt.G > 0) {
      tauSlowNum += pt.G * pt.tau_bulk;
      tauSlowDen += pt.G;
    }
  }
  tauSlow = tauSlowDen > 0 ? tauSlowNum / tauSlowDen : (bulkAbs[0]?.tau_bulk || 100e-9);

  // Use tau_bulk from the thickest p-type layer as tau_slow if computed value seems off
  if (pType.length > 0) {
    const maxTauBulk = Math.max(...pType.map(pt => pt.tau_bulk));
    if (tauSlow > maxTauBulk * 2) tauSlow = maxTauBulk;
  }

  // Ensure tau_fast < tau_slow
  const tf = tauFast != null ? Math.min(tauFast, tauSlow * 0.5) : tauSlow * 0.1;
  const ts = tauSlow;

  // Bi-exponential decay: I(t) = A1*exp(-t/tau_fast) + A2*exp(-t/tau_slow)
  // A1 ∝ depletion fraction, A2 ∝ bulk fraction
  const depFrac = depletion.length / Math.max(lifetimes.length, 1);
  const A1 = Math.max(depFrac, 0.1);
  const A2 = 1.0 - A1;

  const tMax = 5 * ts;
  const nPts = 200;
  const decay = [];
  for (let i = 0; i <= nPts; i++) {
    const t = tMax * i / nPts;
    const I = A1 * Math.exp(-t / tf) + A2 * Math.exp(-t / ts);
    decay.push({ t_ns: t * 1e9, I });
  }

  return {
    decay,
    tau_fast_ns: tf * 1e9,
    tau_slow_ns: ts * 1e9,
    A1, A2,
    depletionFrac: depFrac,
  };
}

export default function PLPanel({ results, theme = 'dark', device = null }) {
  const c = themeColors(theme);
  const colors = c;   // legacy alias consumed by chart JSX below
  const PLTooltip = makePLTooltip(c);
  const axisStyle = { fill: c.plotText, fontSize: 14, fontFamily: 'IBM Plex Mono' };
  const tickStyle = { fill: c.plotText, fontSize: 13, fontFamily: 'IBM Plex Mono' };
  const btnStyle = {
    padding: '5px 12px', fontSize: 12, fontFamily: 'IBM Plex Mono, monospace',
    background: c.btnInactive, border: `1px solid ${c.border}`,
    color: c.btnInactiveText,
    borderRadius: 3, cursor: 'pointer',
  };
  const activeBtnStyle = {
    ...btnStyle, background: c.btnActive, borderColor: c.btnActive,
    color: c.btnPrimaryText, fontWeight: 500,
  };
  const [mode, setMode] = useState('pl');
  const [logScale, setLogScale] = useState(true);
  // PL spectrum intensity display: peak-normalized (default) or raw a.u.
  const [plNorm, setPlNorm] = useState(true);
  // PL spectrum x-axis unit — eV (native) or nm (E = 1239.84/λ), same
  // toggle PLAnalysisPanel offers.
  const [plUnit, setPlUnit] = useState('eV');
  const profiles = useMemo(() => results?.profiles || [], [results]);
  const deviceTraps = useMemo(() => results?.trap_levels || [], [results]);

  // ── Parameter library (known-defects dropdown) ────────────────────
  // Same endpoint / shape as ParameterLibraryPanel. Fetched once; a
  // failure just hides the dropdown (the manual Add-trap form remains).
  const [paramLibrary, setParamLibrary] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/simulation/parameter-library`)
      .then(({ data }) => { if (!cancelled) setParamLibrary(data); })
      .catch(() => { if (!cancelled) setParamLibrary(null); });
    return () => { cancelled = true; };
  }, []);

  // Primary absorber = thickest layer in device.absorber.layers (same
  // rule as App.addDefectToAbsorber). Material string drives the library
  // lookup — no material names are hardcoded here.
  const absorberMaterial = useMemo(() => {
    const layers = device?.absorber?.layers || [];
    if (layers.length === 0) return null;
    let best = layers[0];
    for (const l of layers) {
      if ((l.thickness_um || 0) > (best.thickness_um || 0)) best = l;
    }
    return best.material || best.name || null;
  }, [device]);

  // Library defect list for the active absorber — matched by material id
  // or label (case-insensitive) across every library category.
  const libraryDefects = useMemo(() => {
    if (!paramLibrary || !absorberMaterial) return [];
    const want = String(absorberMaterial).trim().toLowerCase();
    for (const cat of paramLibrary.categories || []) {
      for (const m of cat.materials || []) {
        if (String(m.id || '').toLowerCase() === want ||
            String(m.label || '').toLowerCase() === want) {
          return m.defects || [];
        }
      }
    }
    return [];
  }, [paramLibrary, absorberMaterial]);

  // Local trap state so the user can add / remove defects and watch PL
  // update without re-running the full JV sim.  Seeded from the device's
  // trap_levels whenever a new simulation result arrives.
  const [localTraps, setLocalTraps] = useState(() => deviceTraps);
  const [recalcFlash, setRecalcFlash] = useState(false);
  useEffect(() => { setLocalTraps(deviceTraps); }, [deviceTraps]);

  // "Applied" traps = what actually feeds computePL.  Recalculate copies
  // draft (localTraps) → applied so edits don't redraw the chart on every
  // keystroke.
  const [appliedTraps, setAppliedTraps] = useState(() => deviceTraps);
  useEffect(() => { setAppliedTraps(deviceTraps); }, [deviceTraps]);

  const pl = useMemo(() => computePL(profiles, appliedTraps),
                     [profiles, appliedTraps]);
  const trpl = useMemo(() => computeTRPL(profiles), [profiles]);

  // Data-driven PL x-domain: span of the computed spectrum AND every
  // peak (band-to-band + trap-derived), padded ≥ 0.05 eV / 5% so a
  // newly added defect whose emission lies outside the previous range
  // expands the axis instead of being clipped. (This panel has no
  // manual x-axis override and no nm view — the domain is always
  // recomputed from the current data.)
  const plXDomain = useMemo(() => {
    if (!pl || !pl.spectrum?.length) return ['auto', 'auto'];
    let lo = Infinity, hi = -Infinity;
    for (const s of pl.spectrum) {
      if (s.hv < lo) lo = s.hv;
      if (s.hv > hi) hi = s.hv;
    }
    for (const p of pl.peaks) {
      if (p.eV < lo) lo = p.eV;
      if (p.eV > hi) hi = p.eV;
    }
    if (!(hi > lo)) return ['auto', 'auto'];
    const pad = Math.max(0.05, (hi - lo) * 0.05);
    return [Math.max(0, lo - pad), hi + pad];
  }, [pl]);

  // Display-unit x-domain: eV domain converted to nm when the nm view is
  // active (E = EV_NM/λ inverts the axis order → re-sort lo/hi).
  const plXDomainDisplay = useMemo(() => {
    if (plUnit === 'eV') return plXDomain;
    if (!Array.isArray(plXDomain) ||
        !Number.isFinite(plXDomain[0]) || !Number.isFinite(plXDomain[1])) {
      return ['auto', 'auto'];
    }
    const a = EV_NM / Math.max(plXDomain[0], 1e-6);
    const b = EV_NM / Math.max(plXDomain[1], 1e-6);
    return [Math.min(a, b), Math.max(a, b)];
  }, [plXDomain, plUnit]);

  // Peak markers in display units, with greedy staggered label slots so
  // labels of close-lying peaks don't collide (dy = slot * 13 px).
  const plPeakMarkers = useMemo(() => {
    if (!pl) return [];
    const shown = pl.peaks.filter(pk => pk.eV < 2.0);
    const xs = shown.map(pk => (plUnit === 'eV' ? pk.eV : EV_NM / pk.eV));
    const dom = (Array.isArray(plXDomainDisplay) &&
                 Number.isFinite(plXDomainDisplay[0]) &&
                 Number.isFinite(plXDomainDisplay[1]))
      ? plXDomainDisplay : null;
    const slots = assignLabelSlots(xs, dom);
    return shown.map((pk, i) => ({
      x: xs[i],
      slot: slots[i],
      text: plUnit === 'eV'
        ? `${pk.eV.toFixed(2)} eV`
        : `${Math.round(EV_NM / pk.eV)} nm`,
    }));
  }, [pl, plUnit, plXDomainDisplay]);

  // Absorber Eg for the band-diagram schematic: the solver-reported trap
  // Eg when available, else the strongest band-to-band PL peak.
  const schematicEg = useMemo(() => {
    const trapEg = deviceTraps[0]?.Eg;
    if (Number.isFinite(trapEg)) return trapEg;
    const bb = (pl?.peaks || []).filter(p => p.label === 'Band-to-band');
    if (!bb.length) return null;
    return bb.reduce((a, b) => (b.intensity > a.intensity ? b : a)).eV;
  }, [deviceTraps, pl]);

  // Manual TRPL fit sliders: when autoFit is OFF, overlay a user-tunable
  // bi-exponential on the simulated decay so experimentalists can match
  // measured curves by eye.  Seeded from the simulated τ₁, τ₂ and A₁/A₂.
  const [autoFit, setAutoFit] = useState(true);
  const [manualT1,    setManualT1]    = useState(10);
  const [manualT2,    setManualT2]    = useState(200);
  const [manualRatio, setManualRatio] = useState(1);
  useEffect(() => {
    if (!trpl) return;
    setManualT1(Number(trpl.tau_fast_ns.toFixed(2)));
    setManualT2(Number(trpl.tau_slow_ns.toFixed(2)));
    const r = trpl.A2 > 1e-9 ? trpl.A1 / trpl.A2 : 1;
    setManualRatio(Number(r.toFixed(3)));
  }, [trpl]);

  // Experimental TRPL upload
  const [expTRPL, setExpTRPL] = useState(null);  // array of {t_ns, I}
  const [expMeta, setExpMeta] = useState(null);  // { filename, n, tMax, detectedUnit, unit }
  const [expError, setExpError] = useState(null);
  const [expText, setExpText] = useState(null);  // raw file text — kept so a
                                                 // unit change can re-parse
  const [expUnit, setExpUnit] = useState('auto'); // 'auto' | 'ps' | 'ns' | 'us'
  // Auto-fit boundary flags — parameters that landed on the search-grid
  // edge in the last grid-search run (null = no auto-fit run yet).
  const [fitBoundary, setFitBoundary] = useState(null);
  const fileRef = useRef();

  // ── Stage 2: publish to WorkspaceContext (pl_trpl block) ──────────
  // Debounced 200 ms — slider drags would otherwise emit 60 publishes/s.
  const publish = useContext(WorkspaceContextSetter);
  useEffect(() => {
    const t = setTimeout(() => {
      publish('pl_trpl', {
        mode,
        log_scale: logScale,
        applied_traps: (appliedTraps || []).map(tr => ({
          Et_eV: tr.Et_eV, layer: tr.layer, Nt: tr.Nt, trap_type: tr.trap_type,
        })),
        has_experimental_trpl: !!expTRPL,
        experimental_meta: expMeta,
        experimental_trpl_inline: null,  // Stage 3 enforces inline rule
        auto_fit: autoFit,
        manual_t1_ns: manualT1,
        manual_t2_ns: manualT2,
        manual_ratio: manualRatio,
        recalc_pending: recalcFlash,
      });
    }, 200);
    return () => clearTimeout(t);
  }, [publish, mode, logScale, appliedTraps, expTRPL, expMeta, autoFit,
      manualT1, manualT2, manualRatio, recalcFlash]);

  function applyParsedTRPL(text, filename, unit) {
    const parsed = parseTRPLFile(text, unit);
    if (!parsed || parsed.rows.length < 2) {
      setExpError('Could not parse file — need at least 2 numeric rows (time, intensity).');
      return false;
    }
    setExpTRPL(parsed.rows);
    setExpMeta({ filename, n: parsed.rows.length,
                  tMax: parsed.rows[parsed.rows.length - 1].t_ns,
                  detectedUnit: parsed.detectedUnit,
                  unit: parsed.unit });
    setExpError(null);
    setFitBoundary(null);
    return true;
  }

  async function handleExpFile(file) {
    if (!file) return;
    try {
      const text = await file.text();
      if (applyParsedTRPL(text, file.name, expUnit)) {
        setExpText(text);
        setAutoFit(false);   // flip into manual mode so the overlay renders
      }
    } catch (e) {
      setExpError(`Read failed: ${e.message || e}`);
    }
  }
  // Unit selector change — re-parse the stored file text with the new unit.
  function handleExpUnitChange(unit) {
    setExpUnit(unit);
    if (expText && expMeta) applyParsedTRPL(expText, expMeta.filename, unit);
  }
  function clearExpData() {
    setExpTRPL(null); setExpMeta(null); setExpError(null);
    setExpText(null); setExpUnit('auto'); setFitBoundary(null);
  }

  // χ² of the current manual fit against the uploaded experimental points
  const chi2 = useMemo(() => {
    if (!expTRPL) return null;
    return chiSquared(expTRPL,
      Math.max(manualT1, 1e-3),
      Math.max(manualT2, 1e-3),
      Math.max(manualRatio, 1e-6));
  }, [expTRPL, manualT1, manualT2, manualRatio]);

  function runAutoFit() {
    if (!expTRPL) return;
    const best = autoFitGrid(expTRPL);
    if (!best) return;
    // The search grid steps ~1.62× between τ candidates (±30% quantization),
    // so report 2 significant figures — more digits would imply precision
    // the grid cannot deliver.
    setManualT1(Number(best.tau1.toPrecision(2)));
    setManualT2(Number(best.tau2.toPrecision(2)));
    setManualRatio(Number(best.ratio.toPrecision(2)));
    setFitBoundary(best.atBoundary);
    setAutoFit(false);
  }

  function exportFitCSV() {
    const rows = [
      ['parameter', 'value', 'units'],
      ['tau1', manualT1, 'ns'],
      ['tau2', manualT2, 'ns'],
      ['A1_A2_ratio', manualRatio, ''],
      ['weighted_fit_residual', chi2 == null ? '' : chi2.toFixed(6), ''],
      ['n_points', expTRPL ? expTRPL.length : '', ''],
      ['time_range_ns', expTRPL
        ? `0-${expTRPL[expTRPL.length - 1].t_ns.toFixed(1)}` : '', ''],
    ];
    const blob = new Blob([rows.map(r => r.join(',')).join('\n')],
                          { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'jvista_trpl_fit.csv'; a.click();
    URL.revokeObjectURL(url);
  }

  // Build the manual-fit decay over the same time grid as the simulated
  // decay.  I_manual(t) = A₁·e^(-t/τ₁) + A₂·e^(-t/τ₂), A₁+A₂=1.
  const manualDecay = useMemo(() => {
    if (!trpl || !trpl.decay || !trpl.decay.length) return [];
    const r  = Math.max(manualRatio, 1e-6);
    const A1 = r / (1 + r);
    const A2 = 1  / (1 + r);
    const t1 = Math.max(manualT1, 1e-3);
    const t2 = Math.max(manualT2, 1e-3);
    return trpl.decay.map(({ t_ns }) => ({
      t_ns,
      I: A1 * Math.exp(-t_ns / t1) + A2 * Math.exp(-t_ns / t2),
    }));
  }, [trpl, manualT1, manualT2, manualRatio]);

  if (!profiles.length) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: c.bg, fontFamily: 'IBM Plex Mono', color: c.textDim, fontSize: 14,
      }}>
        Run a simulation to generate PL / TRPL data.
      </div>
    );
  }

  // Wider right margin + extra top so peak-energy ReferenceLine labels
  // don't clip when a peak sits at the edge of the spectrum. The PL chart
  // needs more headroom for the staggered peak labels (3 slots × 13 px).
  const margin = { top: 18, right: 60, bottom: 32, left: 24 };
  const plMargin = { top: 50, right: 60, bottom: 32, left: 24 };

  function addTrap(row) {
    setLocalTraps(ts => [...ts, {
      id: Math.random().toString(36).slice(2),
      trap_type:  row.trap_type,
      Et_eV:      row.Et_eV,
      reference:  row.reference === 'from_Ec' ? 'from_Ec' : 'from_Ev',
      sigma_n:    row.sigma_n,
      sigma_p:    row.sigma_p,
      Nt:         row.Nt,
      // Common name when the trap came from the parameter library —
      // shown in the trap list and the band-diagram schematic.
      name:       row.name || null,
      layer:      deviceTraps[0]?.layer || 'absorber',
      Eg:         deviceTraps[0]?.Eg    || (Number.isFinite(schematicEg) ? schematicEg : 1.50),
    }]);
  }
  function deleteTrap(idx) {
    setLocalTraps(ts => ts.filter((_, i) => i !== idx));
  }
  function recalcPL() {
    setAppliedTraps(localTraps);
    setRecalcFlash(true);
    setTimeout(() => setRecalcFlash(false), 800);
  }

  return (
    <div style={{
      height: '100%', width: '100%', overflow: 'hidden',
      display: 'grid', gridTemplateColumns: '280px 1fr 260px',
      background: c.bg, fontFamily: 'IBM Plex Mono, monospace',
    }}>

      {/* Left sidebar */}
      <div style={{
        borderRight: `1px solid ${c.border}`, overflowY: 'auto', display: 'flex', flexDirection: 'column',
      }}>
        {/* Mode toggle */}
        <div style={{ padding: '16px', borderBottom: `1px solid ${c.border}` }}>
          <div style={{ fontSize: 11, letterSpacing: '2px', textTransform: 'uppercase', color: c.textMuted, marginBottom: 10 }}>Mode</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button style={mode === 'pl' ? activeBtnStyle : btnStyle} onClick={() => setMode('pl')}>PL Spectrum</button>
            <button style={mode === 'trpl' ? activeBtnStyle : btnStyle} onClick={() => setMode('trpl')}>TRPL</button>
          </div>
        </div>

        {/* Y-axis scale */}
        <div style={{ padding: '16px', borderBottom: `1px solid ${c.border}` }}>
          <div style={{ fontSize: 11, letterSpacing: '2px', textTransform: 'uppercase', color: c.textMuted, marginBottom: 10 }}>Y-Axis</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button style={!logScale ? activeBtnStyle : btnStyle} onClick={() => setLogScale(false)}>Linear</button>
            <button style={logScale ? activeBtnStyle : btnStyle} onClick={() => setLogScale(true)}>Log</button>
          </div>
          {mode === 'pl' && (
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <button style={plNorm ? activeBtnStyle : btnStyle} onClick={() => setPlNorm(true)}>Normalized</button>
              <button style={!plNorm ? activeBtnStyle : btnStyle} onClick={() => setPlNorm(false)}>Raw a.u.</button>
            </div>
          )}
          {mode === 'pl' && (
            <>
              <div style={{ fontSize: 11, letterSpacing: '2px', textTransform: 'uppercase', color: c.textMuted, margin: '14px 0 10px' }}>X-Axis</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button style={plUnit === 'eV' ? activeBtnStyle : btnStyle} onClick={() => setPlUnit('eV')}>Energy (eV)</button>
                <button style={plUnit === 'nm' ? activeBtnStyle : btnStyle} onClick={() => setPlUnit('nm')}>λ (nm)</button>
              </div>
            </>
          )}
        </div>

        {/* Results */}
        {mode === 'pl' && pl && (
          <div style={{ padding: '16px', borderBottom: `1px solid ${c.border}` }}>
            <div style={{ fontSize: 11, letterSpacing: '2px', textTransform: 'uppercase', color: c.textMuted, marginBottom: 10 }}>PL Peaks</div>
            {pl.peaks.map((pk, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 14, color: c.text }}>
                <span style={{ color: pk.label === 'Band-to-band' ? c.accent : c.amber }}>{pk.eV.toFixed(2)} eV</span>
                <span style={{ color: c.textMuted, fontSize: 12 }}>
                  {pk.label || (pk.eV < 2.0 ? 'Band-to-band' : 'Window')}
                </span>
              </div>
            ))}
            <div style={{ fontSize: 12, color: c.textDim, marginTop: 10, lineHeight: 1.8 }}>
              B_rad = 2e-10 cm3/s<br />
              Total R_rad = {pl.totalRrad.toExponential(2)} cm-2/s
            </div>
            {!pl.hasTraps && (
              <div style={{ fontSize: 12, color: c.amber, marginTop: 8, lineHeight: 1.6 }}>
                No trap levels defined — only band-to-band emission shown.
              </div>
            )}
          </div>
        )}

        {mode === 'trpl' && trpl && (
          <div style={{ padding: '16px', borderBottom: `1px solid ${c.border}` }}>
            <div style={{ fontSize: 11, letterSpacing: '2px', textTransform: 'uppercase', color: c.textMuted, marginBottom: 10 }}>Heuristic Estimate</div>
            {[
              ['τ₁ (fast)', `${trpl.tau_fast_ns.toFixed(1)} ns`, c.accent],
              ['τ₂ (slow)', `${trpl.tau_slow_ns.toFixed(1)} ns`, c.green],
              ['A₁ (fast)', trpl.A1.toFixed(3), '#8ba3bb'],
              ['A₂ (slow)', trpl.A2.toFixed(3), '#8ba3bb'],
              ['Depletion frac', `${(trpl.depletionFrac * 100).toFixed(0)}%`, '#8ba3bb'],
            ].map(([label, val, color]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 14 }}>
                <span style={{ color: c.textMuted }}>{label}</span>
                <span style={{ color }}>{val}</span>
              </div>
            ))}
            <div style={{ fontSize: 12, color: c.textDim, marginTop: 10, lineHeight: 1.8 }}>
              I(t) = A₁ exp(-t/τ₁)<br />
              &nbsp;&nbsp;&nbsp;&nbsp;+ A₂ exp(-t/τ₂)
            </div>
            <div style={{ fontSize: 12, color: c.amber, marginTop: 8, lineHeight: 1.6 }}>
              Not a transient solve — this bi-exponential is estimated from
              steady-state carrier profiles (depletion vs bulk lifetimes).
            </div>
          </div>
        )}

      </div>

      {/* Main chart area — scrollable so the band-diagram schematic below
          the plot can always be scrolled into view. */}
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', minHeight: 0, overflowY: 'auto' }}>
        {mode === 'pl' ? (
          <>
            <div style={{ fontSize: 14, letterSpacing: '1.5px', textTransform: 'uppercase', color: c.accent, marginBottom: 4, borderBottom: `1px solid ${c.border}`, paddingBottom: 6 }}>
              Steady-State PL Spectrum
            </div>
            {pl && pl.spectrum.length > 0 ? (
              <>
                <div id="pl-chart-container" style={{ flex: '0 0 auto', height: 380 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={pl.spectrum.map(s => {
                      const x = plUnit === 'eV' ? s.hv : EV_NM / Math.max(s.hv, 1e-6);
                      const v = plNorm ? s.I : (s.I_raw ?? s.I);
                      if (!logScale) return { x, I: v };
                      // Log floor 8 decades below the peak in either display.
                      const floor = plNorm ? 1e-8 : pl.Imax * 1e-8;
                      return { x, I: Math.log10(Math.max(v, floor)) };
                    })} margin={plMargin}>
                      <CartesianGrid strokeDasharray="3 3" stroke={colors.plotGrid} />
                      <XAxis dataKey="x" type="number"
                        domain={plXDomainDisplay}
                        tickFormatter={v => plUnit === 'eV' ? v.toFixed(1) : String(Math.round(v))}
                        tick={tickStyle}
                        label={{ value: plUnit === 'eV' ? 'Photon Energy (eV)' : 'Wavelength (nm)',
                          position: 'insideBottom', offset: -16, ...axisStyle }} />
                      <YAxis tickFormatter={v => logScale ? v.toFixed(0) : (plNorm ? v.toFixed(2) : v.toExponential(1))} tick={tickStyle}
                        domain={logScale ? (plNorm ? [-8, 0] : ['auto', 'auto']) : [0, 'auto']}
                        label={{ value: logScale
                            ? (plNorm ? 'log\u2081\u2080(I / I_max)' : 'log\u2081\u2080(I) (a.u.)')
                            : (plNorm ? 'PL intensity (normalized)' : 'PL Intensity (a.u.)'),
                          angle: -90, position: 'insideLeft', offset: -4, textAnchor: 'middle', ...axisStyle }} />
                      <Tooltip content={<PLTooltip xLabel={plUnit === 'eV' ? 'hv' : '\u03bb'} yLabel="I" />} />
                      {plPeakMarkers.map((pk, i) => (
                        <ReferenceLine key={i} x={pk.x} stroke={colors.amber} strokeDasharray="5 3"
                          label={{ value: pk.text, position: 'top', dy: pk.slot * 13,
                            fill: c.amber, fontSize: 12 }} />
                      ))}
                      <Line type="monotone" dataKey="I" stroke={colors.accent} strokeWidth={2} dot={false} name="PL Intensity" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                {Number.isFinite(schematicEg) && (
                  <BandgapDefectSchematic
                    Eg={schematicEg}
                    defects={(appliedTraps || []).map((t, i) => ({
                      id: t.id || i,
                      name: t.name ||
                        `${t.trap_type || 'trap'} ${Number(t.Et_eV ?? 0).toFixed(2)} eV`,
                      Et_eV: t.Et_eV,
                      reference: t.reference,
                    }))}
                    c={c}
                    title="Absorber bandgap & active defect levels"
                  />
                )}
              </>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.textDim, fontSize: 14 }}>
                No PL data — run simulation with illumination
              </div>
            )}
          </>
        ) : (
          <>
            <div style={{ fontSize: 14, letterSpacing: '1.5px', textTransform: 'uppercase', color: c.green, marginBottom: 4, borderBottom: `1px solid ${c.border}`, paddingBottom: 6 }}>
              Time-Resolved PL Decay
              {trpl && (
                <span style={{ marginLeft: 12, fontSize: 13, textTransform: 'none', letterSpacing: 0, color: c.textMuted }}>
                  heuristic τ₁ = {trpl.tau_fast_ns.toFixed(1)} ns, τ₂ = {trpl.tau_slow_ns.toFixed(1)} ns
                </span>
              )}
            </div>
            {trpl && trpl.decay.length > 0 ? (
              <>
                <TRPLUploadBlock
                  c={c} meta={expMeta} error={expError}
                  onPick={(f) => handleExpFile(f)}
                  onClear={clearExpData}
                  fileRef={fileRef}
                  unit={expUnit}
                  onUnitChange={handleExpUnitChange}
                />
                <div id="pl-chart-container" style={{ flex: '0 0 auto', height: 380 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart margin={margin}>
                      <CartesianGrid strokeDasharray="3 3" stroke={colors.plotGrid} />
                      <XAxis dataKey="t_ns" type="number" allowDuplicatedCategory={false}
                        tickFormatter={v => v.toFixed(0)} tick={tickStyle}
                        label={{ value: 'Time (ns)', position: 'insideBottom', offset: -16, ...axisStyle }} />
                      <YAxis tickFormatter={v => logScale ? v.toFixed(0) : v.toFixed(2)} tick={tickStyle}
                        domain={logScale ? [-8, 0] : [0, 1]}
                        label={{ value: logScale ? 'log\u2081\u2080(I / I\u2080)' : 'Normalized PL', angle: -90, position: 'insideLeft', offset: -4, textAnchor: 'middle', ...axisStyle }} />
                      <Tooltip content={<PLTooltip xLabel="t" yLabel="I" />} />
                      <Line type="monotone" data={logScale
                                ? trpl.decay.map(d => ({ ...d, I: d.I > 1e-8 ? Math.log10(d.I) : -8 }))
                                : trpl.decay}
                            dataKey="I" stroke={colors.accent} strokeWidth={2}
                            dot={false}
                            name="Heuristic estimate (from steady-state profiles)" />
                      {!autoFit && (
                        <Line type="monotone" data={logScale
                                ? manualDecay.map(d => ({ ...d, I: d.I > 1e-8 ? Math.log10(d.I) : -8 }))
                                : manualDecay}
                              dataKey="I" stroke={colors.green} strokeWidth={2}
                              strokeDasharray="5 3"
                              dot={false} name="Manual fit" />
                      )}
                      {expTRPL && (
                        <Scatter data={logScale
                                ? expTRPL.map(d => ({ ...d, I: d.I > 1e-8 ? Math.log10(d.I) : -8 }))
                                : expTRPL}
                                 dataKey="I"
                                 fill={c.isDark ? '#ffd700' : '#cc7700'}
                                 name="Measured" />
                      )}
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <TRPLFitControls
                  autoFit={autoFit}
                  onAutoToggle={() => setAutoFit(a => !a)}
                  t1={manualT1} onT1={v => { setManualT1(v); setFitBoundary(null); }}
                  t2={manualT2} onT2={v => { setManualT2(v); setFitBoundary(null); }}
                  ratio={manualRatio} onRatio={v => { setManualRatio(v); setFitBoundary(null); }}
                  simTau1={trpl.tau_fast_ns}
                  simTau2={trpl.tau_slow_ns}
                  chi2={chi2}
                  hasExp={!!expTRPL}
                  onAutoFit={runAutoFit}
                  onExport={exportFitCSV}
                  fitBoundary={fitBoundary}
                  c={c}
                />
              </>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.textDim, fontSize: 14 }}>
                No TRPL data — run simulation first
              </div>
            )}
          </>
        )}

        <PlotExportBar
          chartId="pl-chart-container"
          csvData={
            mode === 'pl' && pl
              ? pl.spectrum.map(s => ({
                  wavelength_nm: 1240 / s.hv,
                  pl_intensity_normalized: s.I,
                  pl_intensity_au: s.I_raw ?? s.I,
                }))
              : (mode === 'trpl' && trpl
                  ? trpl.decay.map(d => ({ time_ns: d.t_ns, trpl_intensity: d.I }))
                  : [])
          }
          csvFilename={mode === 'pl' ? 'jvista_pl_spectrum' : 'jvista_trpl_decay'}
          plotFilename={mode === 'pl' ? 'pl_spectrum' : 'trpl_decay'}
          c={c}
          disabled={mode === 'pl' ? !pl : !trpl}
        />
      </div>

      {/* RIGHT COLUMN — Defect levels editor */}
      <DefectsColumn
        traps={localTraps}
        appliedTraps={appliedTraps}
        onAdd={addTrap}
        onDelete={deleteTrap}
        onRecalc={recalcPL}
        flash={recalcFlash}
        c={c}
        activeBtnStyle={activeBtnStyle}
        libraryDefects={libraryDefects}
        absorberLabel={absorberMaterial}
      />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// TRPL manual-fit control strip — sliders + numeric inputs for τ₁, τ₂
// and the A₁/A₂ ratio.  Toggle chooses whether the plot overlay is
// driven by the simulated fit (auto) or the sliders (manual).
// ───────────────────────────────────────────────────────────────────────────
// ───────────────────────────────────────────────────────────────────────────
// TRPL experimental-data upload block — shown above the chart in TRPL mode.
// ───────────────────────────────────────────────────────────────────────────
function TRPLUploadBlock({ c, meta, error, onPick, onClear, fileRef,
                            unit, onUnitChange }) {
  const [drag, setDrag] = useState(false);
  // "Auto (detected: X)" keeps the current auto-detection as the default
  // while making the tMax-threshold guess visible and overridable.
  const autoLabel = meta?.detectedUnit
    ? `Auto (detected: ${TRPL_UNIT_LABEL[meta.detectedUnit]})`
    : 'Auto-detect';
  return (
    <div style={{
      padding: '10px 12px', marginBottom: 8, borderRadius: 6,
      background: c.bgSecondary, border: `1px dashed ${drag ? c.accent : c.border}`,
      fontFamily: 'IBM Plex Mono, monospace',
    }}
      onDragOver={e => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={e => {
        e.preventDefault(); setDrag(false);
        const f = e.dataTransfer.files?.[0]; if (f) onPick(f);
      }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 18 }} role="img" aria-label="folder">📂</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, color: c.text, fontWeight: 500 }}>
            Upload Measured TRPL Data
          </div>
          {!meta && (
            <div style={{ fontSize: 12, color: c.textMuted, marginTop: 2 }}>
              Drag &amp; drop a CSV/TXT or <button type="button" onClick={() => fileRef.current?.click()}
                style={{ background: 'none', border: 'none', color: c.accent,
                          cursor: 'pointer', padding: 0, fontSize: 12,
                          textDecoration: 'underline',
                          fontFamily: 'IBM Plex Mono, monospace' }}>
                choose file
              </button>.  Two columns: time, intensity.  Time unit auto-detected (ps / ns / µs) — override with the selector.
            </div>
          )}
          {meta && (
            <div style={{ fontSize: 12, color: c.textMuted, marginTop: 2 }}>
              Loaded: <span style={{ color: c.text }}>{meta.filename}</span>
              &nbsp;— {meta.n} points, 0 to {meta.tMax.toFixed(1)} ns
              &nbsp;(read as {TRPL_UNIT_LABEL[meta.unit]})
            </div>
          )}
          {error && (
            <div style={{ fontSize: 12, color: c.red, marginTop: 2 }}>{error}</div>
          )}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6,
                         fontSize: 12, color: c.textMuted }}>
          Time unit:
          <select value={unit}
                  onChange={e => onUnitChange(e.target.value)}
                  style={{ padding: '3px 6px', fontSize: 12,
                            background: c.input, border: `1px solid ${c.border}`,
                            color: c.text, borderRadius: 3,
                            fontFamily: 'IBM Plex Mono, monospace',
                            cursor: 'pointer' }}>
            <option value="auto">{autoLabel}</option>
            <option value="ps">ps</option>
            <option value="ns">ns</option>
            <option value="us">µs</option>
          </select>
        </label>
        {meta && (
          <button onClick={onClear} title="Clear uploaded data"
            style={{ background: 'none', border: 'none', color: c.red,
                      cursor: 'pointer', fontSize: 16, padding: '0 6px' }}>
            ×
          </button>
        )}
      </div>
      <input ref={fileRef} type="file" accept=".csv,.txt,.dat,.tsv"
             style={{ display: 'none' }}
             onChange={e => { const f = e.target.files?.[0]; if (f) onPick(f); }} />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// TRPL manual-fit control strip — log-spaced sliders for τ₁, τ₂, A₁/A₂.
// When experimental data is loaded the strip also shows the weighted fit
// residual, an Auto-fit grid-search button, and an Export CSV for the
// current parameters.
// ───────────────────────────────────────────────────────────────────────────
function TRPLFitControls({ autoFit, onAutoToggle,
                            t1, onT1, t2, onT2, ratio, onRatio,
                            simTau1, simTau2,
                            chi2, hasExp, onAutoFit, onExport,
                            fitBoundary, c }) {
  // Log-scale helpers — sliders run on the log10 domain; we convert
  // to and from real values at the boundary.
  const log = (x) => Math.log10(Math.max(x, 1e-9));
  const pow = (x) => Math.pow(10, x);
  const T1_MIN = 0.1,  T1_MAX = 1000;
  const T2_MIN = 1,    T2_MAX = 10000;
  const R_MIN  = 0.01, R_MAX  = 100;

  const row = { display: 'grid',
                 gridTemplateColumns: '80px 1fr 90px',
                 alignItems: 'center', gap: 8, marginBottom: 6 };
  const lbl = { fontSize: 12, color: c.textMuted,
                 fontFamily: 'IBM Plex Mono, monospace' };
  const inp = { padding: '3px 6px', fontSize: 12,
                 background: c.input, border: `1px solid ${c.border}`,
                 color: c.text, borderRadius: 3,
                 fontFamily: 'IBM Plex Mono, monospace', width: '100%',
                 boxSizing: 'border-box' };

  // Residual colour-coding (heuristic thresholds — lower is better)
  const chi2Color = chi2 == null ? c.textMuted
                  : chi2 < 0.05 ? c.green
                  : chi2 < 0.2  ? c.amber : c.red;

  const smallBtn = {
    padding: '5px 10px', fontSize: 12, borderRadius: 4,
    fontFamily: 'IBM Plex Mono, monospace', cursor: 'pointer',
  };

  return (
    <div style={{
      padding: '10px 14px', borderTop: `1px solid ${c.border}`,
      background: c.bgSecondary, fontFamily: 'IBM Plex Mono, monospace',
    }}>
      <div style={{ display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', marginBottom: 8,
                    gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, letterSpacing: '1.5px',
                         textTransform: 'uppercase', color: c.textMuted }}>
          Manual fit
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                       flexWrap: 'wrap' }}>
          {hasExp && chi2 != null && (
            <span style={{ fontSize: 12, color: chi2Color }}
                  title={'Mean of (I_fit − I)² / max(I, 1e-6) over all points. '
                        + 'Not a statistical reduced χ² (normalized data, no σ², '
                        + 'no degrees-of-freedom correction). Lower is better.'}>
              Fit residual (weighted) = {chi2.toFixed(4)}
            </span>
          )}
          {hasExp && (
            <button onClick={onAutoFit}
              style={{ ...smallBtn, background: c.btnPrimary,
                        color: c.btnPrimaryText, border: 'none',
                        fontWeight: 600 }}>
              Auto-fit to measured
            </button>
          )}
          {hasExp && (
            <button onClick={onExport}
              style={{ ...smallBtn, background: c.btnSecondary,
                        color: c.btnSecondaryText,
                        border: `1px solid ${c.border}` }}>
              Export fit CSV
            </button>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6,
                             fontSize: 12, color: autoFit ? c.green : c.amber,
                             cursor: 'pointer' }}>
            <input type="checkbox" checked={autoFit} onChange={onAutoToggle} />
            <span>Auto fit {autoFit ? 'ON' : 'OFF'}</span>
          </label>
        </div>
      </div>
      {fitBoundary && fitBoundary.length > 0 && (
        <div style={{ fontSize: 11, color: c.amber, marginBottom: 8 }}>
          ⚠ At fit boundary: {fitBoundary.join(', ')} — the grid search hit
          the edge of its range; the true value may lie outside it.
        </div>
      )}
      {!autoFit && (
        <div style={{ fontSize: 11, color: c.textMuted, marginBottom: 8 }}>
          Dashed fit curve tracks the sliders (log-spaced).
          &nbsp;Heuristic-estimate values: τ₁={simTau1.toFixed(1)} ns, τ₂={simTau2.toFixed(1)} ns.
        </div>
      )}

      <div style={row}>
        <span style={lbl}>τ₁ (ns)</span>
        <input type="range" min={log(T1_MIN)} max={log(T1_MAX)} step={0.001}
               value={log(t1)} disabled={autoFit}
               onChange={e => onT1(Number(pow(parseFloat(e.target.value)).toFixed(3)))} />
        <input type="number" value={t1} step="any"
               disabled={autoFit}
               onChange={e => onT1(parseFloat(e.target.value) || 0)}
               style={inp} />
      </div>
      <div style={row}>
        <span style={lbl}>τ₂ (ns)</span>
        <input type="range" min={log(T2_MIN)} max={log(T2_MAX)} step={0.001}
               value={log(t2)} disabled={autoFit}
               onChange={e => onT2(Number(pow(parseFloat(e.target.value)).toFixed(2)))} />
        <input type="number" value={t2} step="any"
               disabled={autoFit}
               onChange={e => onT2(parseFloat(e.target.value) || 0)}
               style={inp} />
      </div>
      <div style={row}>
        <span style={lbl}>A₁ / A₂</span>
        <input type="range" min={log(R_MIN)} max={log(R_MAX)} step={0.001}
               value={log(ratio)} disabled={autoFit}
               onChange={e => onRatio(Number(pow(parseFloat(e.target.value)).toFixed(3)))} />
        <input type="number" value={ratio} step="any"
               disabled={autoFit}
               onChange={e => onRatio(parseFloat(e.target.value) || 0)}
               style={inp} />
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Defect-levels sidebar (rightmost column in PLPanel).  Local state only —
// edits do NOT re-run the JV sim; hitting "Recalculate PL" re-feeds the
// current trap set to computePL client-side and refreshes the spectrum.
// ───────────────────────────────────────────────────────────────────────────
function DefectsColumn({ traps, appliedTraps, onAdd, onDelete, onRecalc, flash,
                          c, activeBtnStyle,
                          libraryDefects = [], absorberLabel = null }) {
  const [trapType, setTrapType] = useState('acceptor');
  const [Et, setEt] = useState('0.75');
  const [etRef, setEtRef] = useState('Ev');   // 'Ev' | 'Ec'
  const [sigN, setSigN] = useState('1e-14');
  const [sigP, setSigP] = useState('1e-14');
  const [Nt, setNt] = useState('1e14');
  const [err, setErr] = useState(null);
  // Parameter-library dropdown selection + the common name it carries
  // into the trap row (cleared once the trap is added).
  const [libSel, setLibSel] = useState('');
  const [libName, setLibName] = useState(null);

  // Selecting a known defect prefills the (fully editable) add form:
  // Et + reference from the library entry; σ 1e-15 cm² and Nt 1e14 cm⁻³
  // defaults unless the library publishes values (defectToTrapRow).
  function handleLibSelect(id) {
    setLibSel(id);
    const d = (libraryDefects || []).find(x => String(x.id) === String(id));
    if (!d) { setLibName(null); return; }
    const row = defectToTrapRow(d);
    setTrapType(row.type);
    setEt(String(row.Et));
    setEtRef(row.Et_ref === 'Ec' ? 'Ec' : 'Ev');
    setSigN(fmtCopy(row.sigma_n));
    setSigP(fmtCopy(row.sigma_p));
    setNt(fmtCopy(row.Nt));
    setLibName(d.label || d.id);
    setErr(null);
  }

  function handleAdd() {
    const Et_eV  = parseFloat(Et);
    const sigma_n = parseFloat(sigN);
    const sigma_p = parseFloat(sigP);
    const Nt_v   = parseFloat(Nt);
    if (![Et_eV, sigma_n, sigma_p, Nt_v].every(Number.isFinite)) {
      setErr('All fields must be numeric'); return;
    }
    if (Et_eV < 0 || Et_eV > 3) {
      setErr('Et must be between 0 and 3 eV'); return;
    }
    setErr(null);
    onAdd({
      trap_type: trapType, Et_eV, sigma_n, sigma_p, Nt: Nt_v,
      reference: etRef === 'Ec' ? 'from_Ec' : 'from_Ev',
      name: libName || null,
    });
    setLibSel('');
    setLibName(null);
  }

  const dirty = JSON.stringify(traps) !== JSON.stringify(appliedTraps);

  const inpStyle = {
    width: '100%', padding: '5px 6px', fontSize: 12,
    background: c.input, border: `1px solid ${c.border}`, color: c.text,
    fontFamily: 'IBM Plex Mono, monospace', borderRadius: 3, boxSizing: 'border-box',
  };
  const lblStyle = { fontSize: 10, color: c.textMuted, marginBottom: 2,
                      textTransform: 'uppercase', letterSpacing: 0.5 };

  return (
    <div style={{ borderLeft: `1px solid ${c.border}`, overflowY: 'auto',
                   display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '16px', borderBottom: `1px solid ${c.border}` }}>
        <div style={{ fontSize: 11, letterSpacing: '2px',
                       textTransform: 'uppercase', color: c.textMuted }}>
          Defect Levels
        </div>
        <div style={{ fontSize: 13, color: c.textMuted, marginTop: 6, lineHeight: 1.5 }}>
          Edits are local — Recalculate PL refreshes the spectrum without
          re-running the JV sim.
        </div>
      </div>

      {/* List */}
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${c.border}` }}>
        {traps.length === 0 ? (
          <div style={{ fontSize: 11, color: c.textDim, padding: '6px 0' }}>
            No trap levels defined.
          </div>
        ) : traps.map((t, i) => (
          <div key={t.id || i}
               style={{ display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', padding: '6px 0',
                        borderBottom: `1px solid ${c.border}`, fontSize: 12,
                        color: c.text }}>
            <div style={{ flex: 1 }}>
              {t.name && (
                <div style={{ fontSize: 11, color: c.text }}>{t.name}</div>
              )}
              <div style={{ color: t.trap_type === 'donor' ? c.accent : c.amber }}>
                {t.trap_type || 'acceptor'} · Et={Number(t.Et_eV ?? 0).toFixed(2)} eV
                {t.reference === 'from_Ec' ? ' (from Ec)' : ''}
              </div>
              <div style={{ fontSize: 10, color: c.textMuted, marginTop: 1 }}>
                Nt={Number(t.Nt || 0).toExponential(1)} cm⁻³
                {t.sigma_n != null && ` · σn=${Number(t.sigma_n).toExponential(0)}`}
              </div>
            </div>
            <button onClick={() => onDelete(i)} title="Remove trap"
                    style={{ background: 'none', border: 'none', color: c.red,
                              cursor: 'pointer', fontSize: 14, padding: '0 4px' }}>
              ×
            </button>
          </div>
        ))}
      </div>

      {/* Known-defects dropdown — populated from the backend parameter
          library for the device's primary absorber material. Selecting
          one prefills the editable Add-trap form below. */}
      {libraryDefects.length > 0 && (
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${c.border}` }}>
          <div style={{ fontSize: 11, color: c.textMuted, marginBottom: 6,
                         textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Known defects for this absorber
            {absorberLabel ? ` — ${absorberLabel}` : ''}
          </div>
          <select value={libSel} onChange={e => handleLibSelect(e.target.value)}
                  style={{ ...inpStyle, cursor: 'pointer' }}>
            <option value="">— select a defect —</option>
            {libraryDefects.map(d => (
              <option key={d.id} value={d.id}>{d.label || d.id}</option>
            ))}
          </select>
          <div style={{ fontSize: 10, color: c.textMuted, marginTop: 4, lineHeight: 1.5 }}>
            Prefills the Add-trap form: Et and reference band from the
            library; σ 1e-15 cm² / Nt 1e14 cm⁻³ defaults — all editable
            before adding.
          </div>
        </div>
      )}

      {/* Add form */}
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${c.border}` }}>
        <div style={{ fontSize: 11, color: c.textMuted, marginBottom: 8,
                       textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Add trap{libName ? ` — ${libName}` : ''}
        </div>
        <div style={{ marginBottom: 8 }}>
          <div style={lblStyle}>Type</div>
          <select value={trapType} onChange={e => setTrapType(e.target.value)}
                  style={{ ...inpStyle, cursor: 'pointer' }}>
            <option value="donor">Donor</option>
            <option value="acceptor">Acceptor</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <div style={{ flex: 1.4 }}>
            <div style={lblStyle}>Et (eV)</div>
            <input style={inpStyle} value={Et} onChange={e => setEt(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={lblStyle}>From</div>
            <select value={etRef} onChange={e => setEtRef(e.target.value)}
                    style={{ ...inpStyle, cursor: 'pointer' }}>
              <option value="Ev">Ev</option>
              <option value="Ec">Ec</option>
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={lblStyle}>σn (cm²)</div>
            <input style={inpStyle} value={sigN} onChange={e => setSigN(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={lblStyle}>σp (cm²)</div>
            <input style={inpStyle} value={sigP} onChange={e => setSigP(e.target.value)} />
          </div>
        </div>
        <div style={{ marginBottom: 10 }}>
          <div style={lblStyle}>Nt (cm⁻³)</div>
          <input style={inpStyle} value={Nt} onChange={e => setNt(e.target.value)} />
        </div>
        {err && (
          <div style={{ fontSize: 11, color: c.red, marginBottom: 6 }}>{err}</div>
        )}
        <button onClick={handleAdd}
                style={{ ...activeBtnStyle, width: '100%', textAlign: 'center' }}>
          + Add trap
        </button>
      </div>

      {/* Recalculate */}
      <div style={{ padding: '12px 16px', marginTop: 'auto' }}>
        <button onClick={onRecalc}
                style={{
                  width: '100%', padding: '8px 16px', fontSize: 13,
                  fontWeight: 600, fontFamily: 'IBM Plex Mono, monospace',
                  borderRadius: 6, border: 'none',
                  background: flash ? c.green : c.btnPrimary,
                  color: c.btnPrimaryText,
                  cursor: 'pointer', opacity: 1,
                }}>
          {flash ? '✓ Recalculated' : 'Recalculate PL'}
        </button>
        {dirty && !flash && (
          <div style={{ fontSize: 12, color: c.amber, marginTop: 6,
                          textAlign: 'center' }}>
            trap edits pending
          </div>
        )}
      </div>
    </div>
  );
}
